import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  PromptToneTemplate,
  PromptToneTemplateUpdate,
} from "@/api/types";

const KEY = ["prompt-tones"] as const;
const KEY_HISTORY = (toneKey: string) =>
  ["prompt-tones-history", toneKey] as const;

export interface PromptVersion {
  version: number | null;
  content: string;
  labels: string[];
  commit_message: string | null;
  created_at: string | null;
}

export function useToneTemplates() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.GET<PromptToneTemplate[]>("/prompt-tones"),
  });
}

export function useToneTemplate(key: string) {
  return useQuery({
    queryKey: [...KEY, key],
    queryFn: () => api.GET<PromptToneTemplate>(`/prompt-tones/${key}`),
    enabled: Boolean(key),
  });
}

export function useUpdateToneTemplate(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PromptToneTemplateUpdate) =>
      api.PATCH<PromptToneTemplate>(`/prompt-tones/${key}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_HISTORY(key) });
    },
  });
}

export function useToneHistory(key: string, enabled: boolean) {
  return useQuery({
    queryKey: KEY_HISTORY(key),
    queryFn: () =>
      api.GET<PromptVersion[]>(`/prompt-tones/${key}/prompt-history`),
    enabled: enabled && Boolean(key),
  });
}

export function useRollbackTone(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { version: number; commit_message?: string }) =>
      api.POST<PromptToneTemplate>(
        `/prompt-tones/${key}/prompt/rollback`,
        payload,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_HISTORY(key) });
    },
  });
}
